export const PDF_PAGE_MARGIN = '1.27cm';

export type DocumentLayoutPreset = {
  headerMarginBottom: string;
  headerPaddingBottom: string;
  infoGridGap: string;
  infoGridMargin: string;
  footerPadding: string;
  footerMarginTop: string;
};

export const STANDARD_DOC_LAYOUT: DocumentLayoutPreset = {
  headerMarginBottom: '4px',
  headerPaddingBottom: '4px',
  infoGridGap: '20px',
  infoGridMargin: '12px 0',
  footerPadding: '12px 0 6px 0',
  footerMarginTop: '12px',
};

/** Historic invoice stamp: large faint emerald PAID, same on staff PDF and public artefact. */
export const PAID_WATERMARK_CSS = `
  .paid-watermark {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-28deg);
    font-size: 120px;
    font-weight: 900;
    letter-spacing: 6px;
    color: rgba(16, 185, 129, 0.14);
    text-transform: uppercase;
    pointer-events: none;
    z-index: 9999;
    user-select: none;
    -webkit-user-select: none;
    white-space: nowrap;
  }
`;

export function isPaidInvoiceDocument(kind?: string | null, status?: string | null): boolean {
  return String(kind || '').trim().toLowerCase() === 'invoice'
    && String(status || '').trim().toLowerCase() === 'paid';
}

export function paidWatermarkMarkup(kind?: string | null, status?: string | null): string {
  return isPaidInvoiceDocument(kind, status)
    ? '<div class="paid-watermark" aria-hidden="true">PAID</div>'
    : '';
}

type BuildSharedDocumentCssArgs = {
  fontFamily: string;
  fontSizeBase: number;
  headerColor: string;
  primaryColor: string;
  secondaryColor: string;
  logoWidth: number;
  logoHeight: number;
  layoutPreset: DocumentLayoutPreset;
  useFlexLayout: boolean;
  includeExportLogo: boolean;
};

export function buildSharedDocumentCss(args: BuildSharedDocumentCssArgs): string {
  const bodyLayout = args.useFlexLayout
    ? `
      display: flex;
      min-height: 100vh;
      flex-direction: column;
    `
    : '';

  const mainContentRule = args.useFlexLayout
    ? `
      #main-content {
        flex: 1 1 auto;
      }
    `
    : '';

  const footerFlexRule = args.useFlexLayout ? 'flex: 0 0 auto;' : '';

  const exportLogoRule = args.includeExportLogo
    ? `
      .export-logo { display: flex; justify-content: flex-end; margin: 0 0 12px 0; }
      .export-logo img { width: ${args.logoWidth}px; height: ${args.logoHeight}px; object-fit: contain; }
    `
    : '';

  return `
    @page { size: A4; margin: ${PDF_PAGE_MARGIN}; }
    html, body {
      height: auto;
      margin: 0;
      padding: 0;
    }
    body {
      ${bodyLayout}
      font-family: ${args.fontFamily};
      color: #333;
      font-size: ${args.fontSizeBase}px;
    }
    ${mainContentRule}
    #doc-footer {
      ${footerFlexRule}
      text-align: center;
      padding: ${args.layoutPreset.footerPadding};
      margin-top: ${args.layoutPreset.footerMarginTop};
    }
    #doc-footer img {
      max-width: 100%;
      height: auto;
      max-height: 150px;
      object-fit: contain;
    }
    .document-template { margin: 0; }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: ${args.layoutPreset.headerMarginBottom};
      border-bottom: 2px solid ${args.headerColor};
      padding-bottom: ${args.layoutPreset.headerPaddingBottom};
    }
    .title { font-size: 24px; font-weight: bold; color: ${args.primaryColor}; }
    .subtitle { font-size: 16px; color: ${args.secondaryColor}; margin-top: 5px; }
    .date { font-size: 13px; color: ${args.secondaryColor}; margin-top: 5px; }
    .invoice-payment-schedule { margin-top: 6px; }
    .payment-terms { font-size: 13px; color: ${args.secondaryColor}; margin-top: 6px; }
    .info-grid { display: flex; gap: ${args.layoutPreset.infoGridGap}; margin: ${args.layoutPreset.infoGridMargin}; }
    .info-section { flex: 1; padding: 0; margin: 0; display: flex; flex-direction: column; }
    .info-section h3 {
      margin: 0 0 8px 0;
      padding: 0;
      font-size: 14px;
      text-transform: uppercase;
      color: ${args.secondaryColor};
      line-height: 1.2;
    }
    .info-section p { margin: 0; padding: 0; line-height: 1.4; flex: 1; }
    .items-table { width: 100%; border-collapse: collapse; margin-top: 16px; font-size: 14px; }
    .items-table th, .items-table td { border: 1px solid #ddd; padding: 12px 8px; text-align: left; }
    .items-table th { background-color: #f8f9fa; font-weight: bold; }
    .items-table tfoot td { font-weight: bold; background-color: #f8f9fa; }
    .total-row { font-weight: bold; font-size: 18px; border-top: 1px solid #ddd; padding-top: 8px; margin-top: 8px; }
    .logo { width: ${args.logoWidth}px; height: ${args.logoHeight}px; object-fit: contain; }
    .header-image { text-align: center; margin: 0 0 8px 0; }
    .header-image img { display: block; margin: 0 auto; max-height: 150px; object-fit: contain; }
    ${exportLogoRule}
    ${PAID_WATERMARK_CSS}
    @media print {
      html, body {
        height: auto !important;
      }
      body {
        margin: 0;
        min-height: 0 !important;
        display: block !important;
      }
      #main-content {
        display: block !important;
        flex: 0 0 auto !important;
      }
      .no-print { display: none; }
    }
  `;
}
